import React, { useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { BsPen, BsUpload } from "react-icons/bs";
import SignaturePad from "./SignaturePad";
import { createCertificateAssetAPI } from "../api/api";

/**
 * Dropdown of saved signatures / seals with "upload new" and (for
 * signatures) "draw new" actions. `value` is the selected asset id.
 */
export default function CertificateAssetPicker({
  label,
  assetType,
  assets,
  value,
  onChange,
  onAssetCreated,
  token,
  allowDraw = false,
}) {
  const [mode, setMode] = useState(null); // "upload" | "draw" | null
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [drawn, setDrawn] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selected = assets.find((asset) => String(asset.id) === String(value));
  const nameLabel = assetType === "seal" ? "Company name" : assetType === "footer" ? "Logo set name" : "Person name";

  const close = () => {
    setMode(null);
    setName("");
    setFile(null);
    setDrawn("");
    setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError(`${nameLabel} is required.`);
      return;
    }
    if (mode === "upload" && !file) {
      setError("Please choose an image file.");
      return;
    }
    if (mode === "draw" && !drawn) {
      setError("Please draw the signature first.");
      return;
    }
    setSaving(true);
    const res = await createCertificateAssetAPI(
      {
        asset_type: assetType,
        name: name.trim(),
        file: mode === "upload" ? file : null,
        image_data: mode === "draw" ? drawn : null,
      },
      token
    );
    setSaving(false);
    if (res.success) {
      onAssetCreated(res.asset);
      onChange(res.asset);
      close();
    } else {
      setError(res.message || "Failed to save.");
    }
  };

  return (
    <div className="cap">
      <Form.Label className="cp-form-label">{label}</Form.Label>
      <div className="cap-row">
        <Form.Select
          className="cp-form-control"
          value={value || ""}
          onChange={(e) => onChange(assets.find((asset) => String(asset.id) === e.target.value) || null)}
        >
          <option value="">— None —</option>
          {assets.map((asset) => (
            <option key={asset.id} value={asset.id}>{asset.name}</option>
          ))}
        </Form.Select>
        <button type="button" className="cp-edit-btn" title="Upload new" onClick={() => setMode("upload")}>
          <BsUpload size={13} /> Upload
        </button>
        {allowDraw && (
          <button type="button" className="cp-edit-btn" title="Draw new" onClick={() => setMode("draw")}>
            <BsPen size={13} /> Draw
          </button>
        )}
      </div>
      <div className="cap-preview">
        {selected ? <img src={selected.image_url} alt={selected.name} /> : <span>No {assetType} selected</span>}
      </div>

      <Modal show={Boolean(mode)} onHide={close} centered dialogClassName="cp-modal" size={mode === "draw" ? "lg" : undefined}>
        <Form onSubmit={save}>
          <Modal.Header closeButton className="cp-modal-header">
            <Modal.Title className="cp-modal-title">
              {mode === "draw" ? "Draw new signature" : `Upload new ${assetType}`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="cp-modal-body">
            {error && <div className="alert cp-modal-alert py-2">{error}</div>}
            <Form.Group className="mb-3">
              <Form.Label className="cp-form-label">{nameLabel}</Form.Label>
              <Form.Control className="cp-form-control" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </Form.Group>
            {mode === "upload" ? (
              <Form.Group className="mb-2">
                <Form.Label className="cp-form-label">Image (.png / .jpg, transparent PNG recommended)</Form.Label>
                <Form.Control className="cp-form-control" type="file" accept=".png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                {file && <img className="cap-upload-preview" src={URL.createObjectURL(file)} alt="preview" />}
              </Form.Group>
            ) : (
              <SignaturePad onChange={setDrawn} />
            )}
          </Modal.Body>
          <Modal.Footer className="cp-modal-footer">
            <button type="button" className="cp-modal-cancel" onClick={close}>Cancel</button>
            <button type="submit" className="cp-modal-submit" disabled={saving}>
              {saving ? <Spinner size="sm" animation="border" /> : "Save"}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
